import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Slide,
  IconButton,
} from '@mui/material';
import './SelectOrganization.css';
import { useTheme } from '../contexts/ThemeContext';
import { Business, ChevronRight } from '@mui/icons-material';
import { getOrganizations, getCurrentUser } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

const SelectOrganization: React.FC = () => {
  const { setCurrentOrg, updateUser } = useAuth();
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoading(true);
      // const params = new URLSearchParams(location.search);
      
      try {
        const response = await getOrganizations();
        if (response && Array.isArray(response.organizations)) {
          setOrganizations(response.organizations);
        } else if (Array.isArray(response)) {
          setOrganizations(response);
        }
        else {
          console.error('Fetched data is not an array:', response);
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Failed to fetch organizations', error);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [location.search]);

  const handleSelectOrganization = async (org: any) => {
    localStorage.setItem('organization', JSON.stringify(org));
    setCurrentOrg(org);
    try {
      const updatedUser = await getCurrentUser(org.id);
      updateUser(updatedUser);
    } catch (error) {
      console.error('Failed to update user after organization selection', error);
    }
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 100,
          height: 100,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: 150,
          height: 150,
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite reverse',
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-10px)' },
        },
      }}
    >
      <Slide direction="up" in timeout={800}>
        <Card
          elevation={0}
          sx={{
            maxWidth: 420,
            width: '100%',
            p: 4,
            borderRadius: 3,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.2)'
              }`,
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 32px 64px -12px rgba(0, 0, 0, 0.4)'
                : '0 32px 64px -12px rgba(0, 0, 0, 0.25)',
          }}
        >
          {loading ? (
            <LoadingSpinner message="Fetching organizations..." />
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 2rem',
                    boxShadow: '0 8px 32px rgba(37, 99, 235, 0.4)',
                    animation: 'float 3s ease-in-out infinite',
                    position: 'relative',
                  }}
                >
                  <Business sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Select Organization
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Choose the organization you want to sign in to.
                </Typography>
              </Box>

              <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                <List>
                  {organizations.map((org, index) => (
                    <React.Fragment key={org.id}>
                      <ListItem
                        disablePadding
                        secondaryAction={
                          <IconButton edge="end" aria-label="select">
                            <ChevronRight />
                          </IconButton>
                        }
                      >
                        <ListItemButton
                          onClick={() => handleSelectOrganization(org)}
                          sx={{
                            borderRadius: 2,
                            transition: 'background-color 0.3s',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemText
                            primary={org.name}
                            secondary={org.description}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < organizations.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </>
          )}
        </Card>
      </Slide>
    </Box>
  );
};

export default SelectOrganization;